import { useEffect, useRef, useState } from 'react';
import { useLegacyAppSession } from '../contexts/LegacyAppSessionContext';
import { getOrganizationByExactName } from './useOrganizations';

const normalizeOrganizationName = (value: unknown) => String(value || '').trim().toLowerCase();

export default function useRequestedOrganizationSelection(requestedOrganizationName: string) {
  const { changeOrganization, org } = useLegacyAppSession();
  const [isResolvingOrganization, setIsResolvingOrganization] = useState(false);
  const [organizationResolutionError, setOrganizationResolutionError] = useState('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestedName = String(requestedOrganizationName || '').trim();
    const normalizedRequestedName = normalizeOrganizationName(requestedName);
    const normalizedCurrentName = normalizeOrganizationName(org?.name);

    if (!normalizedRequestedName || normalizedRequestedName === normalizedCurrentName) {
      requestIdRef.current += 1;
      setIsResolvingOrganization(false);
      setOrganizationResolutionError('');
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;

    const selectRequestedOrganization = async () => {
      setIsResolvingOrganization(true);
      setOrganizationResolutionError('');

      try {
        const requestedOrganization = await getOrganizationByExactName(requestedName);
        if (cancelled || requestIdRef.current !== requestId) return;
        if (!requestedOrganization) {
          throw new Error(`Organization "${requestedName}" was not found.`);
        }

        await changeOrganization(requestedOrganization);
      } catch (error) {
        if (cancelled || requestIdRef.current !== requestId) return;
        console.error('Unable to select organization from user request link.', error);
        setOrganizationResolutionError('The requested organization could not be selected.');
      } finally {
        if (!cancelled && requestIdRef.current === requestId) {
          setIsResolvingOrganization(false);
        }
      }
    };

    selectRequestedOrganization().catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [changeOrganization, org?.name, requestedOrganizationName]);

  return {
    isResolvingOrganization,
    organizationResolutionError,
  };
}
